<template>
  <div class="choice-chips">
    <div class="choice-chips__container">
      <button
        v-for="(option, index) in computedOptions"
        :key="index"
        type="button"
        :class="[
          'choice-chips__item',
          { 'choice-chips__item--active': isSelected(option) },
          { 'choice-chips__item--disabled': computedDisabled },
        ]"
        :disabled="computedDisabled"
        @click="handleSelect(option)"
      >
        {{ getLabel(option) }}
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ChoiceChips',
  props: {
    value: {
      type: [String, Number, Array],
      default: null,
    },
    options: {
      type: Array,
      required: false,
      default: () => [],
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    trackBy: {
      type: String,
      default: 'value',
    },
    label: {
      type: String,
      default: 'label',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    allValue: {
      type: [String, Number],
      default: null,
    },
    context: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    computedValue: {
      get() {
        return this.value !== undefined && this.value !== null
          ? this.value
          : this.context.model || null
      },
      set(newValue) {
        this.$emit('input', newValue)
        if (this.context.model !== undefined) {
          this.context.model = newValue
        }
      },
    },
    computedOptions() {
      const result =
        this.options && this.options.length > 0 ? this.options : this.context.options || []

      return result
    },
    computedMultiple() {
      return this.multiple !== undefined ? this.multiple : this.context.multiple || false
    },
    computedTrackBy() {
      return this.trackBy || this.context.trackBy || 'value'
    },
    computedLabel() {
      return this.label || this.context.label || 'label'
    },
    computedDisabled() {
      return this.disabled !== undefined ? this.disabled : this.context.disabled || false
    },
    computedAllValue() {
      return this.allValue !== null ? this.allValue : this.context.allValue || null
    },
  },
  watch: {
    fields: {
      immediate: true,
      handler(fields) {
        fields.forEach((field) => {
          if (
            field &&
            field.name &&
            this.formValues[field.name] === undefined &&
            field.value !== undefined
          ) {
            this.$emit('update:formValues', { key: field.name, value: field.value })
          }
        })
      },
    },
  },
  mounted() {},
  methods: {
    getLabel(option) {
      if (typeof option === 'object') {
        return this.$t(option[this.computedLabel])
      }
      return this.$t(option)
    },
    getValue(option) {
      if (typeof option === 'object') {
        return option[this.computedTrackBy]
      }
      return option
    },
    isSelected(option) {
      const value = this.getValue(option)

      if (this.computedMultiple) {
        // Handle type coercion for number/string comparison
        return Array.isArray(this.computedValue) && this.computedValue.some((v) => v == value)
      }

      return this.computedValue == value
    },
    handleSelect(option) {
      if (this.computedDisabled) return

      const value = this.getValue(option)

      if (this.computedMultiple) {
        let newValue = Array.isArray(this.computedValue) ? [...this.computedValue] : []

        // Special handling: if allValue is set, it's mutually exclusive with other values
        if (
          this.computedAllValue !== null &&
          (value === this.computedAllValue || value === String(this.computedAllValue))
        ) {
          // If clicking the "all" option, set only that value
          newValue = [this.computedAllValue]
        } else if (this.computedAllValue !== null) {
          // If clicking other values, remove the "all" value if present
          newValue = newValue.filter(
            (v) => v !== this.computedAllValue && v !== String(this.computedAllValue)
          )

          // Toggle the clicked value
          if (newValue.includes(value)) {
            newValue = newValue.filter((v) => v !== value)
          } else {
            newValue.push(value)
          }

          // If no values selected after removal, default to allValue
          if (newValue.length === 0) {
            newValue = [this.computedAllValue]
          }
        } else {
          // Normal multi-select behavior when no allValue is set
          if (newValue.includes(value)) {
            newValue = newValue.filter((v) => v !== value)
          } else {
            newValue.push(value)
          }
        }

        this.computedValue = newValue
      } else {
        // For single selection, allow deselection
        const newValue = this.computedValue === value ? null : value
        this.computedValue = newValue
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/design/variables';

.choice-chips {
  &__container {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  &__item {
    min-width: 50px;
    padding: 5px 15px;
    border: 1px solid $teal-100;
    border-radius: 24px;
    background-color: $white;
    color: $mep-color-text-300;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    outline: none;

    &:hover:not(&--disabled) {
      border-color: $mep-secondary;
      background-color: $mep-secondary-100;
      color: $mep-secondary;
    }

    &--active {
      background-color: $mep-secondary-100;
      border-color: $mep-secondary;
      color: $mep-secondary;

      &:hover:not(.choice-chips__item--disabled) {
        background-color: $mep-secondary-100;
        border-color: $mep-secondary;
      }
    }

    &--disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }
  }
}
</style>
